/**
 * GithubRepoPicker - Custom Backstage Scaffolder Field Extension
 *
 * A dropdown picker that lists GitHub repositories from an organization
 * matching a specified pattern and/or topic.
 *
 * Usage in template.yaml:
 *   ui:field: GithubRepoPicker
 *   ui:options:
 *     owner: badal-io
 *     pattern: 'repo-bu-*'       # Optional: glob pattern to filter by name
 *     topic: infrastructure      # Optional: GitHub topic to filter by (uses Search API - much faster!)
 *     excludeArchived: true      # Optional: exclude archived repos (default: true)
 */

import React from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  CircularProgress,
  TextField,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { useGithubRepos, GithubRepository } from './shared';

export interface GithubRepoPickerOptions {
  owner: string;
  pattern?: string;
  topic?: string;
  excludeArchived?: boolean;
  [key: string]: string | number | boolean | object | any[] | null | undefined;
}

export const GithubRepoPicker = (
  props: FieldExtensionComponentProps<string, GithubRepoPickerOptions>,
) => {
  const { onChange, rawErrors, required, formData, uiSchema, schema } = props;

  const options = uiSchema?.['ui:options'];
  const owner = options?.owner || 'badal-io';
  const pattern = options?.pattern || '*';
  const topic = options?.topic;
  const excludeArchived = options?.excludeArchived !== false;

  const { repos, loading, error } = useGithubRepos(owner, {
    topic,
    pattern,
    excludeArchived,
  });

  const handleChange = (_event: React.ChangeEvent<{}>, value: GithubRepository | null) => {
    onChange(value?.name || '');
  };

  const selectedRepo = repos.find(r => r.name === formData) || null;

  if (loading) {
    return (
      <FormControl fullWidth margin="normal">
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <CircularProgress size={20} />
          <span>Loading repositories...</span>
        </div>
      </FormControl>
    );
  }

  if (error) {
    return (
      <FormControl fullWidth margin="normal" error>
        <InputLabel>{schema.title || 'Repository'}</InputLabel>
        <Select disabled value="">
          <MenuItem value="">Error loading repositories</MenuItem>
        </Select>
        <FormHelperText>{error}</FormHelperText>
      </FormControl>
    );
  }

  return (
    <FormControl fullWidth margin="normal" required={required} error={rawErrors && rawErrors.length > 0}>
      <Autocomplete
        id="github-repo-picker"
        options={repos}
        getOptionLabel={(option) => option.name}
        getOptionSelected={(option, value) => option.name === value.name}
        value={selectedRepo}
        onChange={handleChange}
        renderInput={(params) => (
          <TextField
            {...params}
            label={schema.title || 'Repository'}
            required={required}
            helperText={
              (rawErrors && rawErrors.length > 0 && rawErrors.join(', ')) ||
              schema.description ||
              uiSchema?.['ui:help']
            }
            error={rawErrors && rawErrors.length > 0}
            variant="outlined"
          />
        )}
      />
    </FormControl>
  );
};

export default GithubRepoPicker;
